import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../../services/AuthContext";
import { LEARN_SEEN_EVENT, getSeenLearnPages, markLearnPageSeen } from "../../utils/learnSeen";
import { getLanguageCopy, normalizeLanguage, translatePair } from "../../services/language";
import LearnSeoPage from "../../components/learn/LearnSeoPage";
import LearnShell from "../../components/learn/LearnShell";
import LearnVideos from "../../components/learn/LearnVideos";
import { LEARN_VIDEOS_TOTAL } from "../../content/learnVideos";
import SeoMeta from "../../components/seo/SeoMeta";
import { getLearnSeoPage, learnCompleteNavigationGroups } from "../../content/learnPages";
import { getLearnGuideSearchResults, getLearnGuideSearchTokens } from "../../utils/learnGuideSearch";
import { LEARN_ALL_GUIDE_SLUGS, LEARN_THEMES, learnSlugFromPath } from "../../utils/learnThemes";
import {
  buildFirstLobbyTutorialTarget,
  buildTutorialLevel2Target,
  rememberTutorialReturnPath,
} from "../../utils/firstLobbyTutorial";
import { unlockGameAudio } from "../../utils/gameAudioUnlock";
import {
  LEARN_OG_IMAGE,
  PUBLIC_LEARN_BASE,
  isPublicLearnPathname,
  normalizeLearnContentPath,
  publicLearnRoute,
  resolveLearnPathForMode,
} from "../../utils/learnRoutes";

// Aprender rediseñado (2026-10-18, mezcla elegida por Yosdel de los diseños 1 y 2): pestañas
// arriba, buscador, barra de progreso, tres pasos para empezar, temas con su propio progreso,
// columna de temas en «Guías», y abajo los libros y el canal de YouTube.

const LIBRARY_JSON_URL = "/learn/library.json";
const RULES_BASE_PATH = "/rules";
const LEARN_LAST_UPDATED = "2026-10-18";
const XIMA_PLAY_URL = "https://juegoxima.com/";

const LEARN_HOME_ALTERNATES = [
  { hrefLang: "es", href: "https://juegoxima.com/aprender" },
  { hrefLang: "en", href: "https://juegoxima.com/aprender/en" },
  { hrefLang: "ru", href: "https://juegoxima.com/aprender/ru" },
  { hrefLang: "de", href: "https://juegoxima.com/aprender/de" },
  { hrefLang: "x-default", href: "https://juegoxima.com/aprender" },
];

const asArray = (value) => (Array.isArray(value) ? value : []);

const XIMA_RULES_OBJECTIVE = {
  es: "Llevar el bloque especial a la Cima del tablero.",
  en: "Bring the special block to the top of the board.",
  de: "Bringen Sie den Spezialblock auf die Spitze des Spielbretts.",
  ru: "Доведите специальный блок до вершины игрового поля.",
};

const normalizeRulesPayload = (payload) =>
  asArray(payload)
    .map((entry) => ({
      id: Number(entry?.id),
      text: String(entry?.text || "").trim(),
    }))
    .filter((entry) => Number.isFinite(entry.id) && entry.id > 0 && entry.text)
    .sort((left, right) => left.id - right.id);

const mergeRulesWithSpanishFallback = (localizedRules, spanishRules) => {
  const localizedById = new Map(
    normalizeRulesPayload(localizedRules).map((entry) => [entry.id, entry.text])
  );
  return normalizeRulesPayload(spanishRules).map((entry) => ({
    id: entry.id,
    text: localizedById.get(entry.id) || entry.text,
  }));
};

const loadRulesForLanguage = async (language) => {
  const loadRulesFile = async (languageCode) => {
    const response = await fetch(`${RULES_BASE_PATH}/${languageCode}/xima-rules.json`, {
      method: "GET",
      cache: "no-store",
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.json();
  };

  const spanishRules = await loadRulesFile("es");
  if (language === "es") {
    return normalizeRulesPayload(spanishRules);
  }

  try {
    const localizedRules = await loadRulesFile(language);
    return mergeRulesWithSpanishFallback(localizedRules, spanishRules);
  } catch (error) {
    return normalizeRulesPayload(spanishRules);
  }
};

const HOW_TO_PLAY_VIDEOS = [
  {
    id: "how-to-es",
    youtubeId: "NlSq0nlnHYM",
    duration: "05:32",
    title: {
      es: "Cómo jugar XIMA (español)",
      en: "How to Play XIMA (Spanish)",
      ru: "Как играть в XIMA",
      de: "So spielt man XIMA",
    },
    description: {
      es: "Guía rápida en español para aprender cómo se juega.",
      en: "Quick tutorial in Spanish to learn the gameplay flow.",
      ru: "Короткий урок на испанском, чтобы понять ход игры.",
      de: "Kurzes Tutorial auf Spanisch, um den Spielablauf zu lernen.",
    },
    watchUrl: "https://www.youtube.com/watch?v=NlSq0nlnHYM",
    embedUrl: "https://www.youtube.com/embed/NlSq0nlnHYM",
  },
  {
    id: "how-to-en",
    youtubeId: "bBmdNDj3PpA",
    duration: "08:47",
    title: {
      es: "XIMA en menos de un minuto (inglés)",
      en: "How to Play XIMA in Less Than a Minute",
      ru: "Как играть в XIMA меньше чем за минуту",
      de: "XIMA in weniger als einer Minute",
    },
    description: {
      es: "Resumen en inglés con lo esencial para empezar.",
      en: "English summary with the essentials to start playing.",
      ru: "Краткое объяснение на английском с основами для старта.",
      de: "Kurze englische Zusammenfassung mit allem Wichtigen zum Start.",
    },
    watchUrl: "https://www.youtube.com/watch?v=bBmdNDj3PpA",
    embedUrl: "https://www.youtube.com/embed/bBmdNDj3PpA",
  },
];

// Redes oficiales (orden pedido por Yosdel el 2026-10-18): TikTok, YouTube en español, YouTube en inglés.
const OFFICIAL_CHANNELS = [
  {
    id: "tiktok",
    url: "https://www.tiktok.com/@yojuegoxima",
    icon: "♪",
    tone: "tiktok",
    title: ["TikTok oficial", "Official TikTok"],
    text: ["Videos cortos y novedades de XIMA.", "Short XIMA videos and news."],
  },
  {
    id: "youtube-es",
    url: "https://www.youtube.com/@YVMAxima",
    icon: "▶",
    tone: "red",
    title: ["YouTube en español", "YouTube in Spanish"],
    text: ["Canal oficial de XIMA en español.", "Official XIMA channel in Spanish."],
  },
  {
    id: "youtube-en",
    url: "https://www.youtube.com/@YosdelMuino",
    icon: "▶",
    tone: "red",
    title: ["YouTube en inglés", "YouTube in English"],
    text: ["Canal oficial de XIMA en inglés.", "Official XIMA channel in English."],
  },
];

const TABS = ["empezar", "videos", "reglas", "guias", "libros", "ayuda"];
const STEPS_STORAGE_PREFIX = "xima_learn_steps_v1_";

const readSteps = (userKey) => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(`${STEPS_STORAGE_PREFIX}${userKey}`) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    return {};
  }
};

const writeSteps = (userKey, steps) => {
  try {
    window.localStorage.setItem(`${STEPS_STORAGE_PREFIX}${userKey}`, JSON.stringify(steps));
  } catch (error) {
    // Sin almacenamiento los pasos simplemente no quedan marcados.
  }
};

const sectionAnchorId = (sectionId, index) =>
  `learn-section-${String(sectionId || "section").replace(/[^a-zA-Z0-9_-]/g, "-")}-${index}`;

const getBookCardCopy = (book, language) => {
  const slug = String(book?.slug || "").toLowerCase();
  if (slug.includes("cientifica")) {
    return {
      title: book?.title || "Xima Científica",
      description: translatePair(
        language,
        "Domina la ciencia, las alturas y el control del tablero.",
        "Master science, heights, and board control."
      ),
      theme: "violet",
      image: "/learn/assets/xima-cientifica/image7.png",
    };
  }

  return {
    title: book?.title || "Cómo llegar a la Cima",
    description: translatePair(
      language,
      "Estrategias, alturas, diagonales y caminos ganadores.",
      "Strategies, heights, diagonals, and winning paths."
    ),
    theme: "blue",
    image: "/learn/assets/como-llegar-a-la-xima/image3.png",
  };
};

const percentOf = (part, total) => (total > 0 ? Math.round((part / total) * 100) : 0);

const Learn = ({ publicMode = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const wildcardPath = params["*"];
  const legacyBookSlug = params.bookSlug;
  const rawLearnPath = useMemo(
    () =>
      String(wildcardPath ?? legacyBookSlug ?? "")
        .trim()
        .replace(/^\/+|\/+$/g, "")
        .toLowerCase(),
    [legacyBookSlug, wildcardPath]
  );
  const learnPath = useMemo(() => normalizeLearnContentPath(rawLearnPath), [rawLearnPath]);
  const { language: outletLanguage = "es" } = useOutletContext() || {};
  const language = normalizeLanguage(outletLanguage);
  const isPublicRoute = publicMode || isPublicLearnPathname(location.pathname);
  const fromLobbySource = String(searchParams.get("fromLobbySource") || "").toLowerCase();
  const learnReturnRoute = fromLobbySource === "learn" ? "/game/lobby" : "/game";
  const learnHomeRoute = isPublicRoute ? PUBLIC_LEARN_BASE : "/game/learn";

  const hrefFor = (path) => resolveLearnPathForMode(path, isPublicRoute);

  const goTo = (path, options) => {
    const targetPath = hrefFor(path);
    if (!targetPath) return;
    if (targetPath.startsWith("/#")) {
      if (options?.replace) window.location.replace(targetPath);
      else window.location.assign(targetPath);
      return;
    }
    navigate(targetPath, options);
  };

  const [library, setLibrary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const [rules, setRules] = useState([]);
  const [guideSearchTerm, setGuideSearchTerm] = useState("");

  const t = (es, en) => translatePair(language, es, en);
  const objectiveText = getLanguageCopy(language, XIMA_RULES_OBJECTIVE, XIMA_RULES_OBJECTIVE.es);

  const requestedTab = String(searchParams.get("tab") || "").toLowerCase();
  const activeTab = TABS.includes(requestedTab) ? requestedTab : "empezar";
  const requestedTheme = String(searchParams.get("tema") || "").toLowerCase();
  const activeTheme = LEARN_THEMES.find((theme) => theme.id === requestedTheme) || LEARN_THEMES[0];

  const setView = useCallback(
    (tab, themeId) => {
      const next = new URLSearchParams(searchParams);
      if (tab && tab !== "empezar") next.set("tab", tab);
      else next.delete("tab");
      if (themeId) next.set("tema", themeId);
      else if (tab !== "guias") next.delete("tema");
      setSearchParams(next, { replace: true });
      // Si se eligió desde más abajo (un tema, un paso), se sube hasta las pestañas.
      const tabs = typeof document !== "undefined" ? document.querySelector(".xle-tabs") : null;
      if (tabs && tabs.getBoundingClientRect().top < 0) tabs.scrollIntoView({ block: "start" });
    },
    [searchParams, setSearchParams]
  );

  // Progreso: páginas abiertas (las mismas que cuenta la bolita de «Aprender» del menú).
  const { user: learnUser } = useAuth() || {};
  const userKey = String(learnUser?.loginIdentifier || learnUser?.email || "guest").toLowerCase();
  const [seenSlugs, setSeenSlugs] = useState(() => getSeenLearnPages(userKey));
  const [steps, setSteps] = useState(() => (typeof window === "undefined" ? {} : readSteps(userKey)));

  useEffect(() => {
    setSeenSlugs(getSeenLearnPages(userKey));
    setSteps(readSteps(userKey));
    const refresh = () => setSeenSlugs(getSeenLearnPages(userKey));
    window.addEventListener(LEARN_SEEN_EVENT, refresh);
    return () => window.removeEventListener(LEARN_SEEN_EVENT, refresh);
  }, [userKey]);

  const markStep = useCallback(
    (stepId) => {
      setSteps((current) => {
        if (current[stepId]) return current;
        const next = { ...current, [stepId]: true };
        writeSteps(userKey, next);
        return next;
      });
    },
    [userKey]
  );

  // En el móvil las pestañas y los temas se deslizan de lado: la elegida queda a la vista.
  useEffect(() => {
    if (typeof document === "undefined") return;
    [".xle-tabs .xle-tab.is-on", ".xle-topics .xle-topic.is-on"].forEach((selector) => {
      const item = document.querySelector(selector);
      const row = item?.parentElement;
      if (!item || !row || row.scrollWidth <= row.clientWidth) return;
      const left = item.getBoundingClientRect().left - row.getBoundingClientRect().left + row.scrollLeft;
      if (left < row.scrollLeft || left + item.offsetWidth > row.scrollLeft + row.clientWidth) {
        row.scrollLeft = Math.max(0, left - 12);
      }
    });
  }, [activeTab, activeTheme.id]);

  useEffect(() => {
    if (activeTab === "videos") markStep("videos");
    if (activeTab === "reglas") markStep("rules");
  }, [activeTab, markStep]);

  const showProgress = !isPublicRoute;
  const totalGuides = LEARN_ALL_GUIDE_SLUGS.length;
  const readGuides = LEARN_ALL_GUIDE_SLUGS.filter((slug) => seenSlugs.has(slug)).length;
  const themeStats = useMemo(
    () =>
      new Map(
        LEARN_THEMES.map((theme) => {
          const read = theme.guides.filter((guide) => seenSlugs.has(guide.slug)).length;
          return [theme.id, { read, total: theme.guides.length, unread: theme.guides.length - read }];
        })
      ),
    [seenSlugs]
  );

  const guideSearchTokens = useMemo(() => getLearnGuideSearchTokens(guideSearchTerm), [guideSearchTerm]);
  const isGuideSearchActive = guideSearchTokens.length > 0;
  const searchResults = useMemo(() => {
    if (!isGuideSearchActive) return [];
    return getLearnGuideSearchResults(learnCompleteNavigationGroups, guideSearchTerm).map((item) => {
      const slug = learnSlugFromPath(item.path);
      const page = getLearnSeoPage(slug);
      return { ...item, slug: page?.slug || slug, description: page?.metaDescription || page?.intro || "" };
    });
  }, [guideSearchTerm, isGuideSearchActive]);

  const handleSearchChange = (value) => {
    setGuideSearchTerm(value);
    if (value.trim() && activeTab !== "guias") setView("guias", activeTheme.id);
  };

  useEffect(() => {
    let mounted = true;

    const loadLibrary = async () => {
      setIsLoading(true);
      setErrorMessage("");
      try {
        const response = await fetch(LIBRARY_JSON_URL, { cache: "no-store" });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        if (!mounted) return;
        setLibrary(data);
      } catch (error) {
        if (!mounted) return;
        setErrorMessage(
          language === "es"
            ? "No se pudo cargar la biblioteca interna."
            : "Could not load the internal library."
        );
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    void loadLibrary();

    return () => {
      mounted = false;
    };
  }, [language]);

  useEffect(() => {
    let mounted = true;

    const loadRules = async () => {
      try {
        const nextRules = await loadRulesForLanguage(language);
        if (!mounted) return;
        setRules(nextRules);
      } catch (error) {
        if (!mounted) return;
        setRules([]);
      }
    };

    void loadRules();

    return () => {
      mounted = false;
    };
  }, [language]);

  const books = useMemo(() => asArray(library?.books), [library]);

  const selectedBook = useMemo(
    () => books.find((book) => String(book?.slug || "").toLowerCase() === learnPath) || null,
    [books, learnPath]
  );
  const selectedSeoPage = useMemo(() => getLearnSeoPage(learnPath), [learnPath]);

  // Las direcciones de las guías antiguas (2026-07) llevan a la guía nueva que las reúne.
  useEffect(() => {
    if (!selectedSeoPage?.slug || !learnPath || learnPath === selectedSeoPage.slug) return;
    navigate(`${learnHomeRoute}/${selectedSeoPage.slug}`, { replace: true });
  }, [learnHomeRoute, learnPath, navigate, selectedSeoPage]);
  // Cada página abierta deja de contar en la bolita de «Aprender» del menú.
  useEffect(() => {
    if (publicMode || !selectedSeoPage?.slug) return;
    markLearnPageSeen(learnUser?.loginIdentifier || learnUser?.email || "", selectedSeoPage.slug);
  }, [learnUser, publicMode, selectedSeoPage]);

  const sections = useMemo(() => asArray(selectedBook?.sections), [selectedBook]);
  const toc = useMemo(() => {
    const rawToc = asArray(selectedBook?.toc);
    if (rawToc.length === sections.length && rawToc.length > 0) return rawToc;
    return sections.map((section) => ({ title: section?.title || "", page: section?.page || null }));
  }, [selectedBook, sections]);
  const introduction = useMemo(() => asArray(selectedBook?.introduction), [selectedBook]);

  useEffect(() => {
    setActiveSectionIndex(0);
  }, [learnPath]);

  useEffect(() => {
    if (!isPublicRoute && !isLoading && learnPath && !selectedBook && !selectedSeoPage && books.length > 0) {
      navigate(learnHomeRoute, { replace: true });
    }
  }, [books.length, isLoading, isPublicRoute, learnHomeRoute, learnPath, navigate, selectedBook, selectedSeoPage]);

  const scrollToSection = (index) => {
    if (index < 0 || index >= sections.length) return;
    const section = sections[index];
    const targetId = sectionAnchorId(section?.id, index);
    const target = typeof document !== "undefined" ? document.getElementById(targetId) : null;
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveSectionIndex(index);
  };

  const bookHref = (book) =>
    isPublicRoute ? publicLearnRoute(`biblioteca/${book.slug}`) : `/game/learn/${book.slug}`;

  const openBook = (event, book) => {
    event.preventDefault();
    goTo(bookHref(book));
  };

  const openGuide = (event, guide) => {
    event.preventDefault();
    goTo(guide.path);
  };

  const renderContentBlock = (block, blockIndex) => {
    if (!block || typeof block !== "object") return null;

    if (block.type === "image" && block.src) {
      return (
        <figure key={`img-${blockIndex}`} className="xle-book-figure">
          <img src={block.src} alt={t("Ilustración del libro", "Book illustration")} loading="lazy" />
        </figure>
      );
    }

    if (block.type === "table") {
      const rows = asArray(block.rows);
      if (!rows.length) return null;
      return (
        <div key={`tbl-${blockIndex}`} className="xle-book-table">
          <table>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={`row-${rowIndex}`}>
                  {asArray(row).map((cell, cellIndex) => (
                    <td key={`cell-${rowIndex}-${cellIndex}`}>{cell || "-"}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (block.type === "paragraph" && block.text) {
      if (block.isList) {
        return (
          <div key={`li-${blockIndex}`} className="xle-book-li">
            <span aria-hidden="true" />
            <p>{block.text}</p>
          </div>
        );
      }
      return <p key={`p-${blockIndex}`}>{block.text}</p>;
    }

    return null;
  };

  const backButton = isPublicRoute ? null : (
    <button type="button" className="xle-back" onClick={() => goTo(learnReturnRoute)}>
      ‹ {t("Volver al Lobby", "Back to Lobby")}
    </button>
  );

  if (selectedSeoPage) {
    return <LearnSeoPage page={selectedSeoPage} publicMode={isPublicRoute} />;
  }

  if ((isLoading || errorMessage) && learnPath) {
    return (
      <LearnShell publicMode={isPublicRoute}>
        <div className={`xle-card xle-status ${errorMessage ? "is-error" : ""}`}>
          {errorMessage || t("Cargando biblioteca de Aprender…", "Loading Learn library…")}
        </div>
      </LearnShell>
    );
  }

  if (!isLoading && learnPath && !selectedBook && isPublicRoute) {
    return (
      <LearnShell publicMode>
        <SeoMeta
          title="Página no encontrada | Aprender XIMA"
          description="La guía solicitada no existe en Aprender XIMA. Vuelve al índice para encontrar reglas, modos, tutoriales y soporte."
          canonicalUrl="https://juegoxima.com/aprender"
          ogTitle="Página no encontrada | Aprender XIMA"
          ogDescription="La guía solicitada no existe en Aprender XIMA. Vuelve al índice de Aprender."
          ogImage={LEARN_OG_IMAGE}
          robots="noindex,follow"
          datePublished={LEARN_LAST_UPDATED}
          dateModified={LEARN_LAST_UPDATED}
          structuredData={{
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Página no encontrada | Aprender XIMA",
            description: "La guía solicitada no existe en Aprender XIMA.",
            url: "https://juegoxima.com/aprender",
            isPartOf: { "@type": "WebSite", name: "XIMA", url: "https://juegoxima.com" },
          }}
        />
        <header className="xle-head">
          <div className="xle-ico" aria-hidden="true">📖</div>
          <div className="xle-head-text">
            <h1>Página no encontrada</h1>
            <p>La guía que buscas no existe o todavía no está disponible.</p>
          </div>
        </header>
        <div className="xle-card xle-notfound">
          <p>Si escribiste la dirección a mano, revisa que no falten palabras o guiones.</p>
          <div className="xle-row-buttons">
            <a className="xle-btn is-go" href={PUBLIC_LEARN_BASE}>📖 Volver a Aprender</a>
            <a className="xle-btn" href={`${PUBLIC_LEARN_BASE}/como-jugar`}>Cómo jugar XIMA</a>
            <a className="xle-btn" href={`${PUBLIC_LEARN_BASE}/reglas`}>Reglas de XIMA</a>
            <a className="xle-btn" href={XIMA_PLAY_URL}>🎲 Jugar</a>
          </div>
        </div>
      </LearnShell>
    );
  }

  if (selectedBook) {
    const selectedBookCard = getBookCardCopy(selectedBook, language);

    return (
      <LearnShell publicMode={isPublicRoute}>
        <SeoMeta
          title={`${selectedBook.title} | Biblioteca XIMA`}
          description={selectedBookCard.description}
          canonicalUrl={`https://juegoxima.com/aprender/biblioteca/${selectedBook.slug}`}
          ogTitle={`${selectedBook.title} | Biblioteca XIMA`}
          ogDescription={selectedBookCard.description}
          ogImage={LEARN_OG_IMAGE}
          robots="index,follow"
          datePublished={LEARN_LAST_UPDATED}
          dateModified={LEARN_LAST_UPDATED}
          structuredData={{
            "@context": "https://schema.org",
            "@type": "Article",
            headline: selectedBook.title,
            description: selectedBookCard.description,
            url: `https://juegoxima.com/aprender/biblioteca/${selectedBook.slug}`,
            image: LEARN_OG_IMAGE,
            datePublished: LEARN_LAST_UPDATED,
            dateModified: LEARN_LAST_UPDATED,
          }}
        />
        <nav className="xle-crumbs" aria-label="Breadcrumb">
          <a
            href={hrefFor("/game/learn")}
            onClick={(event) => {
              event.preventDefault();
              goTo("/game/learn?tab=libros");
            }}
          >
            📖 Aprender
          </a>
          <span>›</span>
          <a
            href={hrefFor("/game/learn")}
            onClick={(event) => {
              event.preventDefault();
              goTo("/game/learn?tab=libros");
            }}
          >
            {t("Libros", "Books")}
          </a>
          <span>›</span>
          <b>{selectedBook.title}</b>
        </nav>

        <header className="xle-card xle-book-hero">
          <img className="xle-book-cover" src={selectedBookCard.image} alt="" aria-hidden="true" />
          <div className="xle-book-hero-text">
            <span className="xle-kicker">{t("Libro de XIMA", "XIMA book")}</span>
            <h1>{selectedBook.title}</h1>
            <p>{selectedBookCard.description}</p>
            <div className="xle-row-buttons">
              <button
                type="button"
                className="xle-btn"
                onClick={() => goTo(isPublicRoute ? PUBLIC_LEARN_BASE : "/game/learn?tab=libros")}
              >
                ‹ {t("Volver a Aprender", "Back to Learn")}
              </button>
              {isPublicRoute ? (
                <a href={XIMA_PLAY_URL} className="xle-btn is-go">
                  🎲 {t("Jugar XIMA", "Play XIMA")}
                </a>
              ) : null}
            </div>
          </div>
        </header>

        <section className="xle-book-layout">
          <aside className="xle-card xle-book-toc">
            <div className="xle-side-title">{t("Índice", "Index")}</div>
            <ul>
              {toc.map((item, index) => {
                const label = item?.title || `${t("Sección", "Section")} ${index + 1}`;
                return (
                  <li key={`${label}-${index}`}>
                    <button
                      type="button"
                      onClick={() => scrollToSection(index)}
                      className={index === activeSectionIndex ? "is-on" : ""}
                    >
                      <span>{label}</span>
                      {item?.page ? <small>{t("Pág.", "Page")} {item.page}</small> : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          <div className="xle-book-content">
            <article className="xle-card xle-book-article">
              <h2>{t("Introducción", "Introduction")}</h2>
              <div className="xle-book-blocks">
                {introduction.length > 0 ? (
                  introduction.map((block, index) => renderContentBlock(block, index))
                ) : (
                  <p>
                    {t(
                      "Este libro no tiene introducción marcada. Puedes empezar por la primera sección.",
                      "This book has no explicit introduction marker. You can start with the first section."
                    )}
                  </p>
                )}
              </div>
            </article>

            {sections.map((section, sectionIndex) => (
              <article
                key={`${section.id}-${sectionIndex}`}
                id={sectionAnchorId(section.id, sectionIndex)}
                className="xle-card xle-book-article"
              >
                <div className="xle-book-article-head">
                  <h3>{section.title}</h3>
                  {section.page ? (
                    <span>
                      {t("Pág.", "Page")} {section.page}
                    </span>
                  ) : null}
                </div>
                <div className="xle-book-blocks">
                  {asArray(section.blocks).map((block, blockIndex) => renderContentBlock(block, blockIndex))}
                </div>
              </article>
            ))}

            {sections.length > 0 ? (
              <div className="xle-pager">
                <button
                  type="button"
                  onClick={() => scrollToSection(Math.max(0, activeSectionIndex - 1))}
                  className="xle-btn"
                  disabled={activeSectionIndex <= 0}
                >
                  ‹ {t("Sección anterior", "Previous section")}
                </button>
                <button
                  type="button"
                  onClick={() => scrollToSection(Math.min(sections.length - 1, activeSectionIndex + 1))}
                  className="xle-btn"
                  disabled={activeSectionIndex >= sections.length - 1}
                >
                  {t("Siguiente sección", "Next section")} ›
                </button>
              </div>
            ) : null}
          </div>
        </section>
      </LearnShell>
    );
  }

  const tabLabels = {
    empezar: { icon: "🚀", label: t("Empezar", "Start") },
    videos: { icon: "🎬", label: t("Videos", "Videos"), count: HOW_TO_PLAY_VIDEOS.length + LEARN_VIDEOS_TOTAL },
    reglas: { icon: "📜", label: t("Reglas", "Rules"), count: rules.length || null },
    guias: { icon: "📚", label: t("Guías", "Guides"), count: totalGuides },
    libros: { icon: "📖", label: t("Libros", "Books"), count: books.length || null },
    ayuda: { icon: "❓", label: t("Ayuda", "Help") },
  };

  const renderGuideCard = (guide) => {
    const isRead = seenSlugs.has(guide.slug);
    return (
      <a
        key={guide.path}
        href={hrefFor(guide.path)}
        onClick={(event) => openGuide(event, guide)}
        className={`xle-guide ${showProgress && isRead ? "is-read" : ""}`}
      >
        <strong>{guide.label}</strong>
        {guide.description ? <span className="xle-guide-desc">{guide.description}</span> : null}
        <span className="xle-guide-foot">
          {showProgress ? (
            isRead ? (
              <span className="xle-pill is-green">✓ {t("Leída", "Read")}</span>
            ) : (
              <span className="xle-pill is-gold">● {t("Nueva", "New")}</span>
            )
          ) : (
            <span />
          )}
          <span className="xle-btn is-sm">{t("Abrir", "Open")} ›</span>
        </span>
      </a>
    );
  };

  const renderThemeTile = (theme) => {
    const stats = themeStats.get(theme.id) || { read: 0, total: 0, unread: 0 };
    return (
      <button
        type="button"
        key={theme.id}
        className="xle-tile"
        onClick={() => setView("guias", theme.id)}
      >
        {showProgress && stats.unread > 0 && stats.read > 0 ? (
          <span className="xle-pill is-gold xle-tile-new">
            {stats.unread} {stats.unread === 1 ? t("nueva", "new") : t("nuevas", "new")}
          </span>
        ) : null}
        <span className="xle-tile-icon" aria-hidden="true">{theme.icon}</span>
        <strong>{theme.label}</strong>
        <span className="xle-tile-count">
          {showProgress
            ? `${stats.read} / ${stats.total} ${t("leídas", "read")}`
            : `${stats.total} ${t("guías", "guides")}`}
        </span>
        {showProgress ? (
          <span className="xle-mini" aria-hidden="true">
            <i style={{ width: `${percentOf(stats.read, stats.total)}%` }} />
          </span>
        ) : null}
      </button>
    );
  };

  // Las tarjetas de TikTok y YouTube abren su sub-panel en la pestaña Videos (sin salir de Aprender).
  const openChannelVideos = (event, channelId) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    next.set("tab", "videos");
    next.set("canal", channelId);
    next.delete("tema");
    setSearchParams(next, { replace: true });
    setTimeout(() => {
      const panel = typeof document !== "undefined" ? document.getElementById("mas-videos") : null;
      if (panel) panel.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
  };

  const bottomRow = (
    <section className="xle-bottom">
      {books.map((book) => {
        const card = getBookCardCopy(book, language);
        return (
          <a key={book.slug} href={bookHref(book)} onClick={(event) => openBook(event, book)} className="xle-card xle-book-card">
            <img src={card.image || book.coverImage} alt="" loading="lazy" />
            <span className="xle-book-card-text">
              <strong>{card.title}</strong>
              <span>{card.description}</span>
              <span className="xle-btn is-sm">📖 {t("Leer libro", "Read book")}</span>
            </span>
          </a>
        );
      })}
      {OFFICIAL_CHANNELS.map((channel) => (
        <a
          key={channel.id}
          href={channel.url}
          onClick={(event) => openChannelVideos(event, channel.id)}
          className={`xle-card xle-yt-card is-${channel.tone}`}
        >
          <span className="xle-yt-icon" aria-hidden="true">{channel.icon}</span>
          <span className="xle-book-card-text">
            <strong>{t(...channel.title)}</strong>
            <span>{t(...channel.text)}</span>
            <span className={`xle-btn is-sm is-${channel.tone}`}>🎬 {t("Ver videos", "Watch videos")}</span>
          </span>
        </a>
      ))}
    </section>
  );

  const renderVideoThumb = (video) => (
    <button
      type="button"
      key={video.id}
      className="xle-vthumb"
      onClick={() => setView("videos")}
      style={{ backgroundImage: `url(https://i.ytimg.com/vi/${video.youtubeId}/hqdefault.jpg)` }}
    >
      <span className="xle-vthumb-play" aria-hidden="true">▶</span>
      <span className="xle-vthumb-title">{getLanguageCopy(language, video.title)}</span>
      <small>{video.duration}</small>
    </button>
  );

  const stepDone = (id) => showProgress && Boolean(steps[id]);

  const startTutorial = (level) => {
    unlockGameAudio();
    markStep("practice");
    const target = level === 2 ? buildTutorialLevel2Target() : buildFirstLobbyTutorialTarget();
    rememberTutorialReturnPath(`${window.location.pathname}${window.location.search}`);
    navigate(target.pathname, { state: target.state });
  };

  const renderStart = () => (
    <>
      <h2 className="xle-h2">{t("Empieza aquí · 3 pasos", "Start here · 3 steps")}</h2>
      <section className="xle-steps">
        <div className={`xle-card xle-step ${stepDone("videos") ? "is-done" : ""}`}>
          <div className="xle-step-head">
            <span className="xle-num">{stepDone("videos") ? "✓" : "1"}</span>
            <strong>{t("Mira cómo se juega", "Watch how to play")}</strong>
          </div>
          <div className="xle-vthumbs">{HOW_TO_PLAY_VIDEOS.map(renderVideoThumb)}</div>
        </div>
        <div className={`xle-card xle-step ${stepDone("rules") ? "is-done" : ""}`}>
          <div className="xle-step-head">
            <span className="xle-num">{stepDone("rules") ? "✓" : "2"}</span>
            <strong>{t("Lee las reglas", "Read the rules")}</strong>
          </div>
          <p>
            {t("Objetivo:", "Goal:")} {objectiveText}{" "}
            {rules.length
              ? t(`Son ${rules.length} reglas cortas y numeradas.`, `${rules.length} short numbered rules.`)
              : ""}
          </p>
          <div className="xle-step-actions">
            <button type="button" className="xle-btn" onClick={() => setView("reglas")}>
              📜 {t("Abrir las reglas", "Open the rules")}
            </button>
          </div>
        </div>
        <div className={`xle-card xle-step ${stepDone("practice") ? "is-done" : ""}`}>
          <div className="xle-step-head">
            <span className="xle-num">{stepDone("practice") ? "✓" : "3"}</span>
            <strong>{t("Practica", "Practice")}</strong>
          </div>
          {/* El mismo tutorial guiado que «Empezar tutorial» del lobby (pedido de Yosdel 2026-10-18). */}
          <div className="xle-tutorial">
            <span className="xle-tutorial-badge">🎓 {t("Tutorial guiado", "Guided tutorial")}</span>
            <p>
              {t(
                "¿Primera vez? Te enseña paso a paso a mover el tablero y los bloques, jugando de verdad.",
                "First time? It teaches you step by step how to move the board and the blocks, while you play."
              )}
            </p>
            {isPublicRoute ? (
              <a className="xle-btn is-gold" href={XIMA_PLAY_URL}>
                ▶ {t("Empezar tutorial guiado", "Start guided tutorial")}
              </a>
            ) : (
              <div className="xle-tutorial-actions">
                <button type="button" className="xle-btn is-gold" onClick={() => startTutorial(1)}>
                  ▶ {t("Tutorial 1 · Controles básicos", "Tutorial 1 · Basic controls")}
                </button>
                <button type="button" className="xle-btn is-sm" onClick={() => startTutorial(2)}>
                  {t("Tutorial 2 · Capturas y dados", "Tutorial 2 · Captures and dice")}
                </button>
              </div>
            )}
          </div>
          <p>
            {t(
              "Después juega tu primera partida contra un bot o resuelve un puzzle.",
              "Then play your first game against a bot or solve a puzzle."
            )}
          </p>
          <div className="xle-step-actions">
            {isPublicRoute ? (
              <a className="xle-btn is-go" href={XIMA_PLAY_URL}>
                🎲 {t("Jugar XIMA", "Play XIMA")}
              </a>
            ) : (
              <>
                <button
                  type="button"
                  className="xle-btn is-go"
                  onClick={() => {
                    markStep("practice");
                    navigate("/game/play-bot", { state: { fromLobbySource: "bot", source: "learnStartStep" } });
                  }}
                >
                  🤖 {t("Jugar con bot", "Play vs bot")}
                </button>
                <button
                  type="button"
                  className="xle-btn"
                  onClick={() => {
                    markStep("practice");
                    navigate("/game/puzzles");
                  }}
                >
                  🧩 Puzzle
                </button>
              </>
            )}
          </div>
        </div>
      </section>

      <div className="xle-h2-row">
        <h2 className="xle-h2">{t("Guías por tema", "Guides by topic")}</h2>
        <span className="xle-mute">
          {totalGuides} {t("guías · toca un tema para verlas", "guides · tap a topic to see them")}
        </span>
      </div>
      <section className="xle-tiles">{LEARN_THEMES.map(renderThemeTile)}</section>
      {bottomRow}
    </>
  );

  const renderVideos = () => (
    <>
      <section className="xle-videos">
        {HOW_TO_PLAY_VIDEOS.map((video) => (
          <article key={video.id} className="xle-card xle-video">
            <div className="xle-video-frame">
              <iframe
                title={getLanguageCopy(language, video.title)}
                src={video.embedUrl}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="xle-video-text">
              <strong>{getLanguageCopy(language, video.title)}</strong>
              <span>{getLanguageCopy(language, video.description)}</span>
            </div>
            <a href={video.watchUrl} target="_blank" rel="noreferrer" className="xle-btn is-sm is-red">
              {t("Ver en YouTube", "Watch on YouTube")} ↗
            </a>
          </article>
        ))}
      </section>
      <LearnVideos t={t} />
      {bottomRow}
    </>
  );

  const ruleGuides = (LEARN_THEMES.find((theme) => theme.id === "reglas")?.guides || []).slice(0, 6);

  const renderRules = () => (
    <>
      <section className="xle-card xle-rules">
        <div className="xle-rules-head">
          <strong>📜 {t("Reglas de la Xima", "Rules of Xima")}</strong>
          <span className="xle-mute">
            {t(
              "Reglas para jugar XIMA y llegar a la Cima con inteligencia, estrategia y ciencia.",
              "Rules to play XIMA and reach the summit with intelligence, strategy, and science."
            )}
          </span>
        </div>
        <div className="xle-objective">
          <span>🏁 {t("Objetivo del juego", "Game objective")}</span>
          <p>{objectiveText}</p>
        </div>
        <ol className="xle-rule-list">
          {rules.map((rule) => (
            <li key={`${rule.id}-${rule.text.slice(0, 24)}`}>
              <span>{rule.id}</span>
              <p>{rule.text}</p>
            </li>
          ))}
        </ol>
      </section>
      <div className="xle-h2-row">
        <h2 className="xle-h2">{t("Para entender mejor las reglas", "To understand the rules better")}</h2>
        <button type="button" className="xle-link" onClick={() => setView("guias", "reglas")}>
          {t("Ver todas las guías de reglas", "See all rule guides")} ›
        </button>
      </div>
      <section className="xle-guides">{ruleGuides.map(renderGuideCard)}</section>
      {bottomRow}
    </>
  );

  const renderGuides = () => {
    const stats = themeStats.get(activeTheme.id) || { read: 0, total: 0, unread: 0 };
    return (
      <>
        <section className="xle-guides-layout">
          <aside className="xle-card xle-topics">
            <div className="xle-side-title">{t("Temas", "Topics")}</div>
            {LEARN_THEMES.map((theme) => {
              const themeStat = themeStats.get(theme.id) || { read: 0, total: 0, unread: 0 };
              const isOn = !isGuideSearchActive && theme.id === activeTheme.id;
              return (
                <button
                  type="button"
                  key={theme.id}
                  className={`xle-topic ${isOn ? "is-on" : ""}`}
                  onClick={() => {
                    setGuideSearchTerm("");
                    setView("guias", theme.id);
                  }}
                >
                  <span className="xle-topic-row">
                    <span aria-hidden="true">{theme.icon}</span>
                    <span className="xle-topic-label">{theme.label}</span>
                    <small>{showProgress ? `${themeStat.read}/${themeStat.total}` : themeStat.total}</small>
                  </span>
                  {showProgress ? (
                    <span className="xle-mini" aria-hidden="true">
                      <i style={{ width: `${percentOf(themeStat.read, themeStat.total)}%` }} />
                    </span>
                  ) : null}
                </button>
              );
            })}
          </aside>

          <div className="xle-guides-main">
            {isGuideSearchActive ? (
              <>
                <div className="xle-theme-head">
                  <strong>🔍 {t("Resultados", "Results")}</strong>
                  <span className="xle-mute">
                    {searchResults.length} {t("guías para", "guides for")} «{guideSearchTerm.trim()}»
                  </span>
                  <button type="button" className="xle-btn is-sm" onClick={() => setGuideSearchTerm("")}>
                    ✕ {t("Borrar búsqueda", "Clear search")}
                  </button>
                </div>
                {searchResults.length > 0 ? (
                  <section className="xle-guides">{searchResults.map(renderGuideCard)}</section>
                ) : (
                  <div className="xle-card xle-empty">
                    {t("No encontramos guías con esa búsqueda. Prueba con otra palabra.", "No guides matched that search.")}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="xle-theme-head">
                  <strong>
                    {activeTheme.icon} {activeTheme.label}
                  </strong>
                  <span className="xle-mute">
                    {stats.total} {t("guías", "guides")}
                    {showProgress ? ` · ${stats.unread} ${t("sin leer", "unread")}` : ""}
                  </span>
                </div>
                {activeTheme.sections.map((section) => (
                  <div key={section.label} className="xle-theme-section">
                    {activeTheme.sections.length > 1 ? <h3>{section.label}</h3> : null}
                    <section className="xle-guides">{section.guides.map(renderGuideCard)}</section>
                  </div>
                ))}
              </>
            )}
          </div>
        </section>
        {bottomRow}
      </>
    );
  };

  const renderBooks = () => (
    <section className="xle-books">
      {books.map((book) => {
        const card = getBookCardCopy(book, language);
        const chapters = asArray(book.sections).length;
        return (
          <article key={book.slug} className={`xle-card xle-book-big is-${card.theme}`}>
            <img src={card.image || book.coverImage} alt={card.title} loading="lazy" />
            <div className="xle-book-big-text">
              <strong>{card.title}</strong>
              <span>{card.description}</span>
              {chapters ? (
                <small>
                  {chapters} {t("secciones", "sections")}
                </small>
              ) : null}
              <a href={bookHref(book)} onClick={(event) => openBook(event, book)} className="xle-btn is-go">
                📖 {t("Leer libro", "Read book")}
              </a>
            </div>
          </article>
        );
      })}
    </section>
  );

  const renderHelp = () => (
    <section className="xle-help">
      {OFFICIAL_CHANNELS.map((channel) => (
        <a
          key={channel.id}
          href={channel.url}
          target="_blank"
          rel="noreferrer"
          className="xle-card xle-help-card"
        >
          <span className={`xle-help-icon is-${channel.tone}`} aria-hidden="true">{channel.icon}</span>
          <strong>{t(...channel.title)}</strong>
          <span>{t(...channel.text)}</span>
          <span className={`xle-btn is-sm is-${channel.tone}`}>{t("Visitar canal", "Visit channel")} ↗</span>
        </a>
      ))}
      <button type="button" className="xle-card xle-help-card" onClick={() => setView("guias", "ayuda")}>
        <span className="xle-help-icon" aria-hidden="true">🛠️</span>
        <strong>{t("Solución de problemas", "Troubleshooting")}</strong>
        <span>
          {t(
            "El tablero no carga, el micrófono, la cuenta, la tienda y más.",
            "Board not loading, microphone, account, store and more."
          )}
        </span>
        <span className="xle-btn is-sm">{t("Ver guías", "See guides")} ›</span>
      </button>
      {isPublicRoute ? (
        <a href={XIMA_PLAY_URL} className="xle-card xle-help-card">
          <span className="xle-help-icon is-green" aria-hidden="true">🛟</span>
          <strong>{t("Soporte", "Support")}</strong>
          <span>{t("Entra a XIMA y escríbenos desde Soporte.", "Sign in to XIMA and write to us from Support.")}</span>
          <span className="xle-btn is-sm is-go">{t("Ir a XIMA", "Go to XIMA")} ›</span>
        </a>
      ) : (
        <button type="button" className="xle-card xle-help-card" onClick={() => navigate("/game/support")}>
          <span className="xle-help-icon is-green" aria-hidden="true">🛟</span>
          <strong>{t("Soporte", "Support")}</strong>
          <span>{t("Escríbenos y te respondemos lo antes posible.", "Write to us and we will answer soon.")}</span>
          <span className="xle-btn is-sm is-go">{t("Escribir a soporte", "Contact support")} ›</span>
        </button>
      )}
    </section>
  );

  const tabContent = {
    empezar: renderStart,
    videos: renderVideos,
    reglas: renderRules,
    guias: renderGuides,
    libros: renderBooks,
    ayuda: renderHelp,
  }[activeTab];

  return (
    <LearnShell publicMode={isPublicRoute}>
      <SeoMeta
        title="Aprende a jugar XIMA: reglas, estrategia y guías"
        description="Aprende a jugar XIMA, el juego de mesa de estrategia en 3D: reglas oficiales, modos de juego, estrategia, videos y más de 60 guías paso a paso."
        canonicalUrl="https://juegoxima.com/aprender"
        ogTitle="Aprender XIMA"
        ogDescription="Reglas oficiales, modos de juego, estrategia, videos y guías para aprender a jugar XIMA."
        ogImage={LEARN_OG_IMAGE}
        robots="index,follow"
        datePublished={LEARN_LAST_UPDATED}
        dateModified={LEARN_LAST_UPDATED}
        alternateLinks={LEARN_HOME_ALTERNATES}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Aprender XIMA",
          description: "Reglas, modos, tutoriales y guías para jugar XIMA.",
          url: "https://juegoxima.com/aprender",
          image: LEARN_OG_IMAGE,
          datePublished: LEARN_LAST_UPDATED,
          dateModified: LEARN_LAST_UPDATED,
        }}
      />
      {backButton}
      <header className="xle-head">
        <div className="xle-ico" aria-hidden="true">📖</div>
        <div className="xle-head-text">
          <h1>{t("Aprender", "Learn")}</h1>
          <p>
            {t(
              "Todo lo que necesitas para jugar XIMA y llegar a la Cima, paso a paso.",
              "Everything you need to play XIMA and reach the summit, step by step."
            )}
          </p>
        </div>
        <label className="xle-search">
          <span aria-hidden="true">🔍</span>
          <input
            type="search"
            value={guideSearchTerm}
            onChange={(event) => handleSearchChange(event.target.value)}
            placeholder={t("Busca: reglas, dados, torneos, bot, replay…", "Search: rules, dice, tournaments, bot…")}
            aria-label={t("Buscar dentro de las guías de Aprender", "Search inside Learn guides")}
          />
          {guideSearchTerm ? (
            <button type="button" onClick={() => setGuideSearchTerm("")} aria-label={t("Borrar búsqueda", "Clear search")}>
              ✕
            </button>
          ) : null}
        </label>
        {isPublicRoute ? (
          <a href={XIMA_PLAY_URL} className="xle-btn is-go xle-play" aria-label="Jugar XIMA">
            🎲 {t("Jugar", "Play")}
          </a>
        ) : null}
      </header>

      <nav className="xle-tabs" role="tablist">
        {TABS.map((tab) => {
          const info = tabLabels[tab];
          return (
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === tab}
              key={tab}
              className={`xle-tab ${activeTab === tab ? "is-on" : ""}`}
              onClick={() => {
                if (tab !== "guias") setGuideSearchTerm("");
                setView(tab, tab === "guias" ? activeTheme.id : null);
              }}
            >
              <span aria-hidden="true">{info.icon}</span>
              {info.label}
              {info.count ? <small>{info.count}</small> : null}
            </button>
          );
        })}
      </nav>

      {showProgress ? (
        <section className="xle-progress">
          <strong>{t("Tu progreso", "Your progress")}</strong>
          <span className="xle-bar" aria-hidden="true">
            <i style={{ width: `${percentOf(readGuides, totalGuides)}%` }} />
          </span>
          <span className="xle-mute">
            <b>{readGuides}</b> {t("de", "of")} {totalGuides} {t("guías leídas", "guides read")}
          </span>
          {totalGuides - readGuides > 0 ? (
            <span className="xle-pill is-gold">
              {totalGuides - readGuides} {t("sin leer", "unread")}
            </span>
          ) : (
            <span className="xle-pill is-green">✓ {t("¡Todo leído!", "All read!")}</span>
          )}
        </section>
      ) : null}

      {tabContent()}
    </LearnShell>
  );
};

export default Learn;
